/**
 * Reserve Checker Utility
 * Comprehensive checking of token reserves on both source and destination chains
 * to prevent bridge transactions when reserves are insufficient
 */

import { ethers } from 'ethers';

// ERC20 ABI for token decimals
const ERC20_ABI = [
  {
    "inputs": [],
    "name": "decimals",
    "outputs": [{"internalType": "uint8", "name": "", "type": "uint8"}],
    "stateMutability": "view",
    "type": "function"
  }
];

// Contract ABIs
const DESTINATION_ABI = [
  {
    "inputs": [{"internalType": "address", "name": "sourceToken", "type": "address"}],
    "name": "getTokenMapping",
    "outputs": [{
      "components": [
        {"internalType": "address", "name": "destToken", "type": "address"},
        {"internalType": "uint8", "name": "decimals", "type": "uint8"},
        {"internalType": "bool", "name": "active", "type": "bool"},
        {"internalType": "bool", "name": "isCrossChain", "type": "bool"}
      ],
      "internalType": "struct TokenMapping",
      "name": "",
      "type": "tuple"
    }],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [{"internalType": "address", "name": "token", "type": "address"}],
    "name": "getTokenReserve",
    "outputs": [{"internalType": "uint256", "name": "", "type": "uint256"}],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [{"internalType": "address", "name": "token", "type": "address"}],
    "name": "mintableTokens",
    "outputs": [{"internalType": "bool", "name": "", "type": "bool"}],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [{"internalType": "address", "name": "token", "type": "address"}],
    "name": "getRemainingDailyMintLimit",
    "outputs": [{"internalType": "uint256", "name": "", "type": "uint256"}],
    "stateMutability": "view",
    "type": "function"
  }
];

const SOURCE_ABI = [
  {
    "inputs": [{"internalType": "address", "name": "token", "type": "address"}],
    "name": "getTokenInfo",
    "outputs": [{
      "components": [
        {"internalType": "bool", "name": "supported", "type": "bool"},
        {"internalType": "uint256", "name": "minAmount", "type": "uint256"},
        {"internalType": "uint256", "name": "maxAmount", "type": "uint256"},
        {"internalType": "uint256", "name": "dailyLimit", "type": "uint256"},
        {"internalType": "uint8", "name": "decimals", "type": "uint8"}
      ],
      "internalType": "struct TokenInfo",
      "name": "",
      "type": "tuple"
    }],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [{"internalType": "address", "name": "token", "type": "address"}],
    "name": "getRemainingDailyLimit",
    "outputs": [{"internalType": "uint256", "name": "", "type": "uint256"}],
    "stateMutability": "view",
    "type": "function"
  }
];


/**
 * Safely format a number for display, handling large numbers and decimals
 */
function formatNumberForDisplay(value, maxDecimals = 6) { // maxDecimals for display formatting
  try {
    const num = parseFloat(value);
    if (isNaN(num)) return '0';
    
    // If the number is very large, use scientific notation
    if (Math.abs(num) >= 1e15) {
      return num.toExponential(maxDecimals);
    }
    
    // For regular numbers, limit decimal places
    return num.toFixed(maxDecimals).replace(/\.?0+$/, '');
  } catch (error) {
    console.warn('Error formatting number:', error);
    return '0';
  }
}

/**
 * Validate amount string for bridge operations
 */
function validateAmount(amount, maxDecimals = 18) { // maxDecimals for validation (18 is max for ERC20)
  try {
    // Check if amount is a valid string
    if (typeof amount !== 'string' || amount.trim() === '') {
      return { valid: false, reason: 'Amount must be a non-empty string' };
    }
    
    // Parse as float
    const amountFloat = parseFloat(amount);
    if (isNaN(amountFloat)) {
      return { valid: false, reason: 'Amount must be a valid number' };
    }
    
    if (amountFloat <= 0) {
      return { valid: false, reason: 'Amount must be greater than 0' };
    }
    
    // Check decimal places
    const decimalPlaces = amount.includes('.') ? amount.split('.')[1].length : 0;
    if (decimalPlaces > maxDecimals) {
      return { 
        valid: false, 
        reason: `Amount has too many decimal places (${decimalPlaces}). Maximum allowed: ${maxDecimals}` 
      };
    }
    
    // Check if number is too large
    if (amountFloat >= Number.MAX_SAFE_INTEGER) {
      return { valid: false, reason: 'Amount is too large' };
    }
    
    return { valid: true, amount: amountFloat };
  } catch (error) {
    return { valid: false, reason: `Validation error: ${error.message}` };
  }
}

// Destination bridge contract addresses - now using environment variables
const DESTINATION_BRIDGE_ADDRESSES = {
  '56': process.env.REACT_APP_BSC_DEST_BRIDGE, // BSC Destination Bridge
  '20250217': process.env.REACT_APP_XPHERE_DEST_BRIDGE // Xphere Destination Bridge
};

// Token addresses for reserve checking - now using environment variables
const TOKEN_ADDRESSES = {
  '56': {
    USDT: process.env.REACT_APP_USDT_ADDRESS, // BSC USDT
    USDX: process.env.REACT_APP_USDX_ADDRESS  // BSC USDX
  },
  '20250217': {
    USDT: process.env.REACT_APP_USDT_ADDRESS, // Xphere USDT
    USDX: process.env.REACT_APP_USDX_ADDRESS  // Xphere USDX
  }
};

/**
 * Get RPC provider for a specific chain
 */
function getRpcProvider(chainId) {
  const chainIdStr = chainId.toString();
  
  if (chainIdStr === '56') {
    // BSC Mainnet
    return new ethers.JsonRpcProvider(process.env.REACT_APP_BSC_MAINNET_RPC_URL || 'https://api-bsc-mainnet-full.n.dwellir.com/c6fd7c04-24d6-4cb1-881b-c3c3a43ba755');
  } else if (chainIdStr === '20250217') {
    // Xphere Mainnet
    return new ethers.JsonRpcProvider(process.env.REACT_APP_XPHERE_MAINNET_RPC_URL || 'https://xp-mainnet.rpc.xplorium.xyz');
  } else {
    throw new Error(`Unsupported chain: ${chainId}`);
  }
}

/**
 * Check if bridge is available based on reserves and limits
 * @param {Object} params - Bridge parameters
 * @param {number} params.sourceChainId - Source chain ID
 * @param {number} params.destChainId - Destination chain ID
 * @param {string} params.sourceTokenAddress - Source token address
 * @param {string} params.sourceContractAddress - Source bridge contract address
 * @param {string} params.destContractAddress - Destination bridge contract address
 * @param {string} params.amount - Amount to bridge (in human readable format)
 * @returns {Object} - Bridge availability status
 */
export async function checkBridgeAvailability({
  sourceChainId,
  destChainId,
  sourceTokenAddress,
  sourceContractAddress,
  destContractAddress,
  amount
}) {
  console.log('🔍 Checking bridge availability...');
  console.log(`   Source: ${sourceChainId} -> Destination: ${destChainId}`);
  console.log(`   Token: ${sourceTokenAddress}`);
  console.log(`   Amount: ${amount}`);

  // Validate amount first
  const amountValidation = validateAmount(amount);
  if (!amountValidation.valid) {
    return {
      available: false,
      reason: amountValidation.reason,
      details: { amount, validation: amountValidation }
    };
  }

  try {
    // Get providers for both chains
    const sourceProvider = getRpcProvider(sourceChainId);
    const destProvider = getRpcProvider(destChainId);

    // Create contract instances
    const sourceContract = new ethers.Contract(sourceContractAddress, SOURCE_ABI, sourceProvider);
    const destContract = new ethers.Contract(destContractAddress, DESTINATION_ABI, destProvider);

    // Get token info from source contract
    const sourceTokenInfo = await sourceContract.getTokenInfo(sourceTokenAddress);
    console.log('✅ Source token info:', {
      supported: sourceTokenInfo.supported,
      decimals: sourceTokenInfo.decimals,
      minAmount: sourceTokenInfo.minAmount.toString(),
      maxAmount: sourceTokenInfo.maxAmount.toString(),
      dailyLimit: sourceTokenInfo.dailyLimit.toString()
    });

    if (!sourceTokenInfo.supported) {
      return {
        available: false,
        reason: 'Token not supported on source chain',
        details: { sourceTokenInfo }
      };
    }

    // Convert amount to proper decimals with safe decimal handling
    let bridgeAmount;
    try {
      // First, parse the amount as a float to handle any decimal precision issues
      const amountFloat = parseFloat(amount);
      if (isNaN(amountFloat) || amountFloat <= 0) {
        return {
          available: false,
          reason: 'Invalid amount provided',
          details: { amount, amountFloat }
        };
      }
      
      // Convert to string with proper decimal places, then parse units
      const decimals = parseInt(sourceTokenInfo.decimals.toString(), 10);
      if (isNaN(decimals) || decimals < 0 || decimals > 18) { // 18 is max for ERC20 tokens
        throw new Error(`Invalid token decimals: ${sourceTokenInfo.decimals}`);
      }
      const amountString = amountFloat.toFixed(decimals);
      bridgeAmount = ethers.parseUnits(amountString, decimals);
    } catch (error) {
      console.error('❌ Error parsing amount:', error);
      return {
        available: false,
        reason: `Error parsing amount: ${error.message}`,
        details: { amount, error: error.message }
      };
    }

    // Check source chain daily limits
    try {
      const remainingSourceLimit = await sourceContract.getRemainingDailyLimit(sourceTokenAddress);
      console.log('✅ Source daily limit remaining:', remainingSourceLimit.toString());
      
      if (remainingSourceLimit < bridgeAmount) {
        return {
          available: false,
          reason: 'Source chain daily limit exceeded',
          details: { 
            requested: bridgeAmount.toString(),
            remaining: remainingSourceLimit.toString(),
            limit: sourceTokenInfo.dailyLimit.toString()
          }
        };
      }
    } catch (error) {
      console.warn('⚠️ Could not check source daily limit:', error.message);
    }

    // Get destination token mapping
    const tokenMapping = await destContract.getTokenMapping(sourceTokenAddress);
    console.log('✅ Destination token mapping:', {
      destToken: tokenMapping[0],
      decimals: tokenMapping[1],
      active: tokenMapping[2],
      isCrossChain: tokenMapping[3]
    });

    if (!tokenMapping.active) {
      return {
        available: false,
        reason: 'Token mapping not active on destination chain',
        details: { tokenMapping }
      };
    }

    const destTokenAddress = tokenMapping[0];

    // Check if destination token is mintable
    const isMintable = await destContract.mintableTokens(destTokenAddress);
    console.log('✅ Destination token mintable:', isMintable);

    if (!isMintable) {
      // Check destination reserves for non-mintable tokens
      const destReserves = await destContract.getTokenReserve(destTokenAddress);
      console.log('✅ Destination reserves:', destReserves.toString());

      if (destReserves === 0n) {
        return {
          available: false,
          reason: 'No reserves available on destination chain',
          details: {
            destToken: destTokenAddress,
            reserves: destReserves.toString(),
            isMintable: false
          }
        };
      }

      if (destReserves < bridgeAmount) {
        return {
          available: false,
          reason: 'Insufficient reserves on destination chain',
          details: {
            destToken: destTokenAddress,
            requested: bridgeAmount.toString(),
            available: destReserves.toString(),
            isMintable: false
          }
        };
      }
    } else {
      // For mintable tokens, check daily mint limits
      try {
        const remainingDestLimit = await destContract.getRemainingDailyMintLimit(destTokenAddress);
        console.log('✅ Destination daily mint limit remaining:', remainingDestLimit.toString());

        if (remainingDestLimit < bridgeAmount) {
          return {
            available: false,
            reason: 'Destination chain daily mint limit exceeded',
            details: {
              destToken: destTokenAddress,
              requested: bridgeAmount.toString(),
              remaining: remainingDestLimit.toString(),
              isMintable: true
            }
          };
        }
      } catch (error) {
        console.warn('⚠️ Could not check destination daily mint limit:', error.message);
      }
    }

    // All checks passed
    console.log('✅ Bridge is available for transaction');
    return {
      available: true,
      reason: 'Bridge is available',
      details: {
        sourceTokenInfo,
        tokenMapping,
        isMintable,
        bridgeAmount: bridgeAmount.toString()
      }
    };

  } catch (error) {
    console.error('❌ Error checking bridge availability:', error);
    
    // Provide more specific error messages based on error type
    let reason = 'Unknown error occurred';
    if (error.message.includes('too many decimals')) {
      reason = 'Amount has too many decimal places for this token';
    } else if (error.message.includes('NUMERIC_FAULT')) {
      reason = 'Invalid number format for bridge amount';
    } else if (error.message.includes('underflow')) {
      reason = 'Amount is too small or has invalid precision';
    } else if (error.message.includes('overflow')) {
      reason = 'Amount is too large for this token';
    } else {
      reason = `Error checking bridge availability: ${error.message}`;
    }
    
    return {
      available: false,
      reason,
      details: { 
        error: error.message,
        errorType: error.code || 'UNKNOWN',
        amount: amount,
        sourceChainId,
        destChainId,
        sourceTokenAddress
      }
    };
  }
}

/**
 * Check bridge availability with correct reserve checking based on direction
 * BSC → Xphere: Check USDX reserves in Xphere destination
 * Xphere → BSC: Check USDT reserves in BSC destination
 * 
 * Note: Both USDT and USDX are non-mintable tokens, so we only check reserves
 * 
 * @param {string} tokenSymbol - The symbol of the token being bridged (e.g., 'USDX', 'USDT', 'RWANOVA')
 */
export async function checkBridgeAvailabilityWithDirection({
  sourceChainId,
  destChainId,
  sourceTokenAddress,
  amount,
  tokenSymbol = null
}) {
  console.log('🔍 Checking bridge availability with direction-based reserve checking...');
  console.log(`   Source: ${sourceChainId} -> Destination: ${destChainId}`);
  console.log(`   Token: ${sourceTokenAddress}`);
  console.log(`   Amount: ${amount}`);

  // Validate amount first
  const amountValidation = validateAmount(amount);
  if (!amountValidation.valid) {
    return {
      available: false,
      reason: amountValidation.reason,
      details: { amount, validation: amountValidation }
    };
  }

  try {
    // Determine the destination chain and token to check
    const sourceChainStr = sourceChainId.toString();
    const destChainStr = destChainId.toString();
    
    // Get the destination bridge contract address
    const destBridgeAddress = DESTINATION_BRIDGE_ADDRESSES[destChainStr];
    if (!destBridgeAddress) {
      return {
        available: false,
        reason: `Unsupported destination chain: ${destChainStr}`,
        details: { destChainStr }
      };
    }

    // Get destination token mapping to determine which token to check
    const destProvider = getRpcProvider(destChainId);
    const destContract = new ethers.Contract(destBridgeAddress, DESTINATION_ABI, destProvider);
    
    // Get the destination token mapping from the contract
    const tokenMapping = await destContract.getTokenMapping(sourceTokenAddress);
    const destTokenAddress = tokenMapping[0];
    const isActive = tokenMapping[2];
    
    if (!isActive) {
      return {
        available: false,
        reason: 'Token mapping not active on destination chain',
        details: { tokenMapping }
      };
    }
    
    // Use provided token symbol, or fallback to legacy hardcoded logic for backward compatibility
    let tokenToCheck;
    let tokenName;
    
    if (tokenSymbol) {
      // Use the actual token symbol being bridged
      tokenName = tokenSymbol.toUpperCase();
      tokenToCheck = destTokenAddress;
      console.log(`🔄 Bridge direction: ${sourceChainStr} → ${destChainStr}, checking ${tokenName} reserves`);
      console.log(`📍 Checking ${tokenName} reserves in destination contract:`, destBridgeAddress);
      console.log(`📍 ${tokenName} token address:`, tokenToCheck);
    } else {
      // Legacy fallback for backward compatibility (USDT/USDX only)
      if (sourceChainStr === '56' && destChainStr === '20250217') {
        // BSC → Xphere: Check USDX reserves in Xphere destination
        tokenToCheck = TOKEN_ADDRESSES['20250217'].USDX;
        tokenName = 'USDX';
        console.log('🔄 Bridge direction: BSC → Xphere, checking USDX reserves in Xphere destination');
        console.log('📍 Checking USDX reserves in Xphere destination contract:', DESTINATION_BRIDGE_ADDRESSES['20250217']);
        console.log('📍 USDX token address:', tokenToCheck);
      } else if (sourceChainStr === '20250217' && destChainStr === '56') {
        // Xphere → BSC: Check USDT reserves in BSC destination
        tokenToCheck = TOKEN_ADDRESSES['56'].USDT;
        tokenName = 'USDT';
        console.log('🔄 Bridge direction: Xphere → BSC, checking USDT reserves in BSC destination');
        console.log('📍 Checking USDT reserves in BSC destination contract:', DESTINATION_BRIDGE_ADDRESSES['56']);
        console.log('📍 USDT token address:', tokenToCheck);
      } else {
        return {
          available: false,
          reason: `Unsupported bridge direction: ${sourceChainStr} → ${destChainStr}`,
          details: { sourceChainStr, destChainStr }
        };
      }
    }

    console.log('🌐 Destination chain provider:', destChainStr);
    console.log('🏗️ Destination bridge contract address:', destBridgeAddress);

    // Get token decimals dynamically
    const tokenContract = new ethers.Contract(tokenToCheck, ERC20_ABI, destProvider);
    const tokenDecimals = await tokenContract.decimals();
    
    // Convert amount to proper decimals using actual token decimals
    const amountFloat = parseFloat(amount);
    const tokenDecimalsNumber = parseInt(tokenDecimals.toString(), 10);
    const amountString = amountFloat.toFixed(tokenDecimalsNumber);
    const bridgeAmount = ethers.parseUnits(amountString, tokenDecimalsNumber);

    // Check if token is mintable
    const isMintable = await destContract.mintableTokens(tokenToCheck);
    console.log(`✅ ${tokenName} is ${isMintable ? 'mintable' : 'non-mintable'}`);

    if (isMintable) {
      // For mintable tokens, check daily mint limits
      try {
        const remainingDestLimit = await destContract.getRemainingDailyMintLimit(tokenToCheck);
        console.log(`✅ ${tokenName} daily mint limit remaining:`, remainingDestLimit.toString());

        if (remainingDestLimit < bridgeAmount) {
          return {
            available: false,
            reason: `${tokenName} daily mint limit exceeded on destination chain`,
            details: {
              token: tokenName,
              tokenAddress: tokenToCheck,
              requested: bridgeAmount.toString(),
              remaining: remainingDestLimit.toString(),
              isMintable: true,
              bridgeDirection: `${sourceChainStr} → ${destChainStr}`
            }
          };
        }
      } catch (error) {
        console.warn(`⚠️ Could not check ${tokenName} daily mint limit:`, error.message);
      }
    } else {
      // For non-mintable tokens, check reserves
      const reserves = await destContract.getTokenReserve(tokenToCheck);
      console.log(`✅ ${tokenName} reserves on destination:`, reserves.toString());

      if (reserves === 0n) {
        return {
          available: false,
          reason: `No ${tokenName} reserves available on destination chain`,
          details: {
            token: tokenName,
            tokenAddress: tokenToCheck,
            reserves: reserves.toString(),
            isMintable: false,
            bridgeDirection: `${sourceChainStr} → ${destChainStr}`
          }
        };
      }

      if (reserves < bridgeAmount) {
        return {
          available: false,
          reason: `Insufficient ${tokenName} reserves on destination chain`,
          details: {
            token: tokenName,
            tokenAddress: tokenToCheck,
            requested: bridgeAmount.toString(),
            available: reserves.toString(),
            isMintable: false,
            bridgeDirection: `${sourceChainStr} → ${destChainStr}`
          }
        };
      }
    }

    // All checks passed
    console.log('✅ Bridge is available for transaction');
    return {
      available: true,
      reason: 'Bridge is available',
      details: {
        token: tokenName,
        tokenAddress: tokenToCheck,
        bridgeAmount: bridgeAmount.toString(),
        bridgeDirection: `${sourceChainStr} → ${destChainStr}`,
        isMintable: isMintable
      }
    };

  } catch (error) {
    console.error('❌ Error checking bridge availability:', error);
    
    return {
      available: false,
      reason: `Error checking bridge availability: ${error.message}`,
      details: { 
        error: error.message,
        amount: amount,
        sourceChainId,
        destChainId,
        sourceTokenAddress
      }
    };
  }
}

/**
 * Check if any bridge pair is available (for UI display)
 * @param {Object} contractAddresses - Contract addresses mapping
 * @returns {Object} - Overall bridge status
 */
export async function checkOverallBridgeStatus(contractAddresses) {
  console.log('🔍 Checking overall bridge status...');

  const bridgePairs = [
    {
      name: 'BSC -> Xphere (USDT -> USDX)',
      sourceChainId: 56,
      destChainId: 20250217,
      sourceToken: contractAddresses[56]?.usdt,
      sourceContract: contractAddresses[56]?.sourceBridge,
      destContract: contractAddresses[20250217]?.destBridge
    },
    {
      name: 'Xphere -> BSC (USDX -> USDT)',
      sourceChainId: 20250217,
      destChainId: 56,
      sourceToken: contractAddresses[20250217]?.usdx,
      sourceContract: contractAddresses[20250217]?.sourceBridge,
      destContract: contractAddresses[56]?.destBridge
    }
  ];

  const results = {};

  for (const pair of bridgePairs) {
    if (!pair.sourceToken || !pair.sourceContract || !pair.destContract) {
      results[pair.name] = {
        available: false,
        reason: 'Contract addresses not configured'
      };
      continue;
    }

    try {
      // Check with a small test amount (1 token)
      const testAmount = '1';
      const status = await checkBridgeAvailability({
        sourceChainId: pair.sourceChainId,
        destChainId: pair.destChainId,
        sourceTokenAddress: pair.sourceToken,
        sourceContractAddress: pair.sourceContract,
        destContractAddress: pair.destContract,
        amount: testAmount
      });

      results[pair.name] = status;
    } catch (error) {
      results[pair.name] = {
        available: false,
        reason: `Error checking bridge: ${error.message}`
      };
    }
  }

  const overallAvailable = Object.values(results).some(result => result.available);
  
  return {
    overallAvailable,
    details: results
  };
}

const reserveChecker = {
  checkBridgeAvailability,
  checkBridgeAvailabilityWithDirection,
  checkOverallBridgeStatus,
  validateAmount,
  formatNumberForDisplay
};

export default reserveChecker;
